import { observer } from 'mobx-react-lite';
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../hooks/useStore';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { FormikTextField } from '../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikDropdown } from '../../../../components/form-components/common-components/dropdown/FormikDropdown';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import styles from './NewAppointmentPopup.module.css';
import { AppointmentSchema } from '../../../../schemas/AppointmentSchema';
import { Step1HospitalTagSelectSchema } from '../../../../schemas/AppointmentStepperSchema';
import {
  AppointmentFormValues,
  appointmentInitialValues,
} from '../../../../models/appointment/AppointmentModel';
import { PersonFormValues, personInitialValues } from '../../../../models/person/PersonModel';
import { toast } from 'react-toastify';
import { PersonSchema } from '../../../../schemas/PersonSchema';
import { AppointmentTimeFields } from './AppointmentTimeFields';
import { userManagementService } from '../../../../services/modules/userManagement/userManagement.service';
import { IUserManagementResult } from '../../../../models/user-management/UserManagementModel';
import { hospitalService } from '../../../../services/modules/hospital/hospital.service';
import { ITag } from '../../../../models/hospital/HospitalModel';

enum StepType {
  SelectHospitalTag = 0,
  Person = 1,
  Appointment = 2,
}

const STEPS = ['Hastane ve Etiket Seç', 'Kişi Oluştur', 'Randevu Bilgileri'];

interface Step1FormValues {
  hospitalId: number | null;
  tagId: number | null;
}

const HospitalTagLoader = ({
  hospitalId,
  onHospitalChange,
  onClearTags,
}: {
  hospitalId: number | null;
  onHospitalChange: (id: number) => void;
  onClearTags: () => void;
}) => {
  const { setFieldValue } = useFormikContext<Step1FormValues>();
  useEffect(() => {
    if (hospitalId) {
      onHospitalChange(hospitalId);
      setFieldValue('tagId', null);
    } else {
      onClearTags();
      setFieldValue('tagId', null);
    }
  }, [hospitalId, onHospitalChange, onClearTags, setFieldValue]);
  return null;
};

interface Step2Response {
  id: number;
  name: string;
}

const PopupFooter = ({
  onClose,
  activeStep,
  formSubmitButtonRef,
  isLoading,
}: {
  onClose: () => void;
  activeStep: StepType;
  formSubmitButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  isLoading: boolean;
}) => {
  const handleSaveClick = () => {
    formSubmitButtonRef.current?.click();
  };

  return (
    <>
      <CommonButton type="cancel" onClick={onClose} disabled={isLoading}>
        İptal
      </CommonButton>
      <CommonButton type="save" onClick={handleSaveClick} disabled={isLoading} loading={isLoading}>
        {activeStep < StepType.Appointment ? 'Kaydet ve Devam Et' : 'Kaydet'}
      </CommonButton>
    </>
  );
};

const AppointmentForHospitalTagPopup = () => {
  const { popupStore, hospitalStore, uiStore, personStore, appointmentStore } = useStore();
  const [activeStep, setActiveStep] = useState<StepType>(StepType.SelectHospitalTag);
  const [step1Data, setStep1Data] = useState<{
    hospitalId: number;
    tagId: number;
    tagPrefix: string;
  } | null>(null);
  const [step2Data, setStep2Data] = useState<Step2Response | null>(null);
  const [tags, setTags] = useState<ITag[]>([]);
  const [users, setUsers] = useState<IUserManagementResult[]>([]);
  const [isStep1Submitting, setIsStep1Submitting] = useState(false);
  const [isStep2Submitting, setIsStep2Submitting] = useState(false);
  const formSubmitButtonRef = useRef<HTMLButtonElement | null>(null);

  const resetStepper = useCallback(() => {
    setActiveStep(StepType.SelectHospitalTag);
    setStep1Data(null);
    setStep2Data(null);
    setTags([]);
  }, []);

  useEffect(() => {
    if (!popupStore.isAppointmentForHospitalTagOpen) {
      resetStepper();
      return;
    }
  }, [popupStore.isAppointmentForHospitalTagOpen, resetStepper]);

  useEffect(() => {
    const fetchHospitals = async () => {
      if (!popupStore.isAppointmentForHospitalTagOpen) return;
      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals({
          startRow: 0,
          endRow: 1000,
          filterModel: null,
          sortModel: null,
        });
      } catch (err) {
        console.error('Error fetching hospitals:', err);
      } finally {
        uiStore.setLoading(false);
      }
    };
    fetchHospitals();
  }, [popupStore.isAppointmentForHospitalTagOpen, hospitalStore, uiStore]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!popupStore.isAppointmentForHospitalTagOpen || activeStep !== StepType.Appointment)
        return;
      try {
        const usersList = await userManagementService.getAllUsers();
        const actualUsers = Array.isArray(usersList)
          ? usersList
          : ((usersList as { value?: IUserManagementResult[] })?.value ?? []);
        setUsers(actualUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, [popupStore.isAppointmentForHospitalTagOpen, activeStep]);

  const hospitalOptions = useMemo(
    (): Array<{ value: number; label: string }> =>
      hospitalStore.hospitalList
        .filter((h): h is { id: number; hospitalName?: string } => h.id !== undefined)
        .map(h => ({ value: h.id, label: h.hospitalName || 'Hastane' })),
    [hospitalStore.hospitalList]
  );

  const tagOptions = useMemo(
    (): Array<{ value: number; label: string }> =>
      tags.map(t => ({ value: t.id, label: t.prefix })),
    [tags]
  );

  const loadTagsForHospital = useCallback(async (hospitalId: number) => {
    try {
      const tagList = await hospitalService.getTagsByHospitalId(hospitalId);
      setTags(tagList || []);
    } catch (err) {
      console.error('Error fetching tags:', err);
      setTags([]);
    }
  }, []);

  const onClearTags = useCallback(() => setTags([]), []);

  const step1InitialValues: Step1FormValues = {
    hospitalId: null,
    tagId: null,
  };

  const handleStep1Submit = async (values: Step1FormValues) => {
    if (!values.hospitalId || !values.tagId) return;
    const selectedTag = tags.find(t => t.id === values.tagId);
    if (!selectedTag) return;
    setStep1Data({
      hospitalId: values.hospitalId,
      tagId: values.tagId,
      tagPrefix: selectedTag.prefix,
    });
    setActiveStep(StepType.Person);
  };

  const handleStep2Submit = async (values: PersonFormValues) => {
    if (!step1Data?.hospitalId) return;
    try {
      setIsStep2Submitting(true);
      uiStore.setLoading(true);
      const response = await personStore.createPerson({
        ...values,
        hospitalId: step1Data.hospitalId,
      });

      if (response.error !== true && response.value) {
        setStep2Data({
          id: response.value.id!,
          name: response.value.name || '',
        });
        toast.success('Kişi başarıyla oluşturuldu');
        setActiveStep(StepType.Appointment);
      }
    } catch (error) {
      console.error('Kişi oluşturulurken hata:', error);
    } finally {
      setIsStep2Submitting(false);
      uiStore.setLoading(false);
    }
  };

  const handleAppointmentSubmit = async (values: AppointmentFormValues) => {
    try {
      if (!step1Data?.hospitalId || !step2Data?.id) {
        toast.error('Lütfen hastane, etiket ve kişi oluşturun');
        return;
      }

      const response = await appointmentStore.createAppointment({
        ...values,
        hospitalId: step1Data.hospitalId,
        personId: step2Data.id,
        tagNumberId: step1Data.tagId,
        requestNumber: step1Data.tagPrefix,
      });

      if (response.error !== true) {
        popupStore.setIsAppointmentForHospitalTagOpen(false);
        resetStepper();
      }
    } catch (error) {
      console.error('Randevu oluşturulurken hata:', error);
    }
  };

  const handleClose = () => {
    popupStore.setIsAppointmentForHospitalTagOpen(false);
    resetStepper();
    toast.dismiss();
  };

  const personInitialValuesWithHospital = useMemo(
    () => ({
      ...personInitialValues,
      hospitalId: step1Data?.hospitalId ?? null,
    }),
    [step1Data]
  );

  const appointmentInitialValuesWithStepData = useMemo(
    () => ({
      ...appointmentInitialValues,
      hospitalId: step1Data?.hospitalId ?? null,
      personId: step2Data?.id ?? null,
      requestNumber: step1Data?.tagPrefix ?? '',
    }),
    [step1Data, step2Data]
  );

  const personOptionsForStep3 = useMemo(() => {
    if (!step2Data) return [];
    return [{ value: step2Data.id, label: step2Data.name }];
  }, [step2Data]);

  const tagOptionsForStep3 = useMemo(() => {
    if (!step1Data?.tagPrefix) return [];
    return [{ value: step1Data.tagPrefix, label: step1Data.tagPrefix }];
  }, [step1Data]);

  const userOptionsForStep3 = useMemo(
    (): Array<{ value: string; label: string }> =>
      users
        .filter(u => u.id && !u.isDeleted)
        .map(u => ({
          value: u.id,
          label: `${u.userName}${u.firstName && u.lastName ? ` (${u.firstName} ${u.lastName})` : ''}`,
        })),
    [users]
  );

  const isLoading = uiStore.isLoading || isStep1Submitting || isStep2Submitting;

  return (
    <Popup
      isOpen={popupStore.isAppointmentForHospitalTagOpen}
      onClose={handleClose}
      title="Hastane ve Etiket Numarasına Randevu Oluştur"
      footer={() => (
        <PopupFooter
          onClose={handleClose}
          activeStep={activeStep}
          formSubmitButtonRef={formSubmitButtonRef}
          isLoading={isLoading}
        />
      )}
    >
      <div className={styles.container}>
        <Stepper activeStep={activeStep} alternativeLabel className={styles.stepper}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === StepType.SelectHospitalTag && (
          <Formik
            initialValues={step1InitialValues}
            validationSchema={Step1HospitalTagSelectSchema}
            onSubmit={handleStep1Submit}
            enableReinitialize
          >
            {({ values }) => (
              <>
                <HospitalTagLoader
                  hospitalId={values.hospitalId}
                  onHospitalChange={loadTagsForHospital}
                  onClearTags={onClearTags}
                />
                <Form className={styles.form}>
                  <button
                    ref={formSubmitButtonRef}
                    type="submit"
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    aria-hidden
                  />
                  <div className={styles.stepContent}>
                    <div className={styles.inputContainer}>
                      <FormikDropdown
                        name="hospitalId"
                        options={hospitalOptions}
                        label="Hastane"
                        placeholder="Hastane seçiniz"
                        required
                      />
                    </div>
                    <div className={styles.inputContainer}>
                      <FormikDropdown
                        name="tagId"
                        options={tagOptions}
                        label="Etiket Ön Eki"
                        placeholder="Önce hastane seçiniz"
                        disabled={!values.hospitalId}
                        required
                      />
                    </div>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        )}

        {activeStep === StepType.Person && step1Data && (
          <Formik
            initialValues={personInitialValuesWithHospital}
            validationSchema={PersonSchema}
            onSubmit={handleStep2Submit}
          >
            {() => (
              <Form className={styles.form}>
                <button
                  ref={formSubmitButtonRef}
                  type="submit"
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  aria-hidden
                />
                <div className={styles.stepContent}>
                  {uiStore.isLoading && (
                    <div className={styles.loadingOverlay}>
                      <div className={styles.spinner} />
                      <span>Yükleniyor...</span>
                    </div>
                  )}
                  <div className={styles.inputContainer}>
                    <FormikDropdown
                      name="hospitalId"
                      options={hospitalOptions}
                      label="Hastane"
                      disabled
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <FormikTextField name="name" label="İsim" placeholder="İsim giriniz" required />
                  </div>
                  <div className={styles.inputContainer}>
                    <FormikTextField
                      name="phoneNumber"
                      label="Telefon Numarası"
                      placeholder="(5XX) XXX XX XX"
                      type="tel"
                      mask={(value: string) => {
                        const numbers = value.replace(/\D/g, '');
                        if (numbers.length <= 3) {
                          return numbers.length > 0 ? `(${numbers}` : '';
                        } else if (numbers.length <= 6) {
                          return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
                        } else if (numbers.length <= 8) {
                          return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
                        } else {
                          return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(
                            6,
                            8
                          )} ${numbers.slice(8, 10)}`;
                        }
                      }}
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <FormikTextField
                      name="email"
                      label="E-mail"
                      placeholder="E-mail giriniz (opsiyonel)"
                      type="email"
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}

        {activeStep === StepType.Appointment && step1Data && step2Data && (
          <Formik
            initialValues={appointmentInitialValuesWithStepData}
            validationSchema={AppointmentSchema}
            onSubmit={handleAppointmentSubmit}
          >
            {() => (
              <Form className={styles.form}>
                <button
                  ref={formSubmitButtonRef}
                  type="submit"
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  aria-hidden
                />
                <div className={styles.stepContent}>
                  {uiStore.isLoading && (
                    <div className={styles.loadingOverlay}>
                      <div className={styles.spinner} />
                      <span>Yükleniyor...</span>
                    </div>
                  )}
                  <div className={styles.inputContainer}>
                    <FormikDropdown
                      name="requestNumber"
                      options={tagOptionsForStep3}
                      label="Etiket Numarası"
                      disabled
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <FormikDropdown
                      name="personId"
                      options={personOptionsForStep3}
                      label="Kişi"
                      disabled
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <FormikDropdown
                      name="userId"
                      options={userOptionsForStep3}
                      label="Kullanıcı"
                      placeholder="Kullanıcı seçiniz"
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <FormikTextField
                      name="appointmentDate"
                      label="Randevu Tarihi"
                      type="date"
                      placeholder="Tarih seçiniz"
                      required
                    />
                  </div>
                  <AppointmentTimeFields />
                  <div className={styles.inputContainer}>
                    <FormikTextField
                      name="deviceList"
                      label="Cihaz Listesi"
                      placeholder="Cihaz listesini giriniz"
                      multiline
                      rows={4}
                      required
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <FormikTextField
                      name="notes"
                      label="Notlar"
                      placeholder="Notları giriniz (opsiyonel)"
                      multiline
                      rows={2}
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </Popup>
  );
};

export default observer(AppointmentForHospitalTagPopup);
